import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Label from 'components/atoms/Label';
import RangeSlider from 'components/atoms/RangeSlider';
import { Dispatch } from 'redux';
import { DataDisplayType, EnviroDisplayType, PortType, RootState } from 'types';
import { GridColumnInterface } from 'types/functions';
import { ActionsType } from 'types/system';
import { clone, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface GridValueFilterModelProps {
    children?: string | JSX.Element | JSX.Element[];

    //unused props
    language: string;
    id?: string;
    data: any;
    actions: ActionsType | undefined;
    port: PortType;
}

interface GridValueFilterModelState {
    column: GridColumnInterface;
    filter: {
        min: number;
        max: number;
    };
    dataDisplay: DataDisplayType;
    enviroDisplay: EnviroDisplayType;
    range: number[];
}

class GridValueFilterModel extends Component<GridValueFilterModelProps, GridValueFilterModelState> {
    constructor(props: GridValueFilterModelProps) {
        super(props);

        const filter = props.data.filter[props.data.column.uuid]
            ? props.data.filter[props.data.column.uuid]
            : {
                  min: props.data.min,
                  max: props.data.max,
              };
        this.state = {
            enviroDisplay: props.port.meta_data.enviroDisplay,
            column: clone(props.data.column),
            filter: filter,
            range: [
                props.data.min < filter.min ? props.data.min : filter.min,
                props.data.max > filter.max ? props.data.max : filter.max,
            ],
            dataDisplay: props.port.meta_data.dataDisplay,
        };
    }

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        const { filter } = this.props.data;
        setKey(filter, this.props.data.column.uuid, this.state.filter);
        this.props.actions?.onConfirm(filter);
        this.props.actions?.onCancel();
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    handleFormDelete = () => {
        const filterObject = { ...this.props.data.filter };

        // Remove the key from filterObject
        delete filterObject[this.props.data.column.uuid];

        this.props.actions?.onConfirm(filterObject);
        // Call the onCancel action, if it exists
        this.props.actions?.onCancel();
    };

    handleSliderChange = (names: string[], values: number[]): void => {
        const filterObject = { ...this.state.filter }; // Clone the filterObject

        names.forEach((name, index) => {
            const value = values[index];

            // Set the current key's value
            setKey(filterObject, name, value);
        });

        this.setState({ filter: filterObject });
    };

    getDisplay = () => {
        if (this.state?.column?.meta_data?.port_url_id) {
            return this.state.enviroDisplay[this.state?.column.meta_data.option][
                this.state?.column.meta_data.parameter
            ];
        } else {
            return this.state.dataDisplay.transit[this.state.column.meta_data.option];
        }
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Grid filter')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>

                    <FormGroup>
                        <Label>{this.props.data.column.label}</Label>

                        <RangeSlider
                            onChange={(name, values) => {
                                const setting = this.getDisplay();
                                this.handleSliderChange(
                                    name,
                                    values.map((x) => {
                                        return x / setting.database2display_unit_factor;
                                    })
                                );
                            }}
                            name={['min', 'max']}
                            unit={this.getDisplay().unit}
                            label={this.getDisplay().name}
                            decimal={this.getDisplay().decimal}
                            value={[
                                parseFloat(
                                    (this.state.filter.min * this.getDisplay().database2display_unit_factor).toFixed(
                                        this.getDisplay().decimal
                                    )
                                ),
                                parseFloat(
                                    (this.state.filter.max * this.getDisplay().database2display_unit_factor).toFixed(
                                        this.getDisplay().decimal
                                    )
                                ),
                            ]}
                            range={[
                                parseFloat(
                                    (this.state.range[0] * this.getDisplay().database2display_unit_factor).toFixed(
                                        this.getDisplay().decimal
                                    )
                                ),
                                parseFloat(
                                    (this.state.range[1] * this.getDisplay().database2display_unit_factor).toFixed(
                                        this.getDisplay().decimal
                                    )
                                ),
                            ]}
                        />
                    </FormGroup>

                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button
                                className="px-4 py-2 bg-primary-delete rounded-md text-white"
                                onClick={this.handleFormDelete}
                            >
                                {translate('Delete filter')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const gridValueFilterModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    port: state.port.port,
});

const gridValueFilterModelDispatchToProps = (dispatch: Dispatch) => ({});

export default connect(gridValueFilterModelStateToProps, gridValueFilterModelDispatchToProps)(GridValueFilterModel);
